import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  FiArrowRight,
  FiBarChart2,
  FiChevronDown,
  FiCreditCard,
  FiTarget,
} from "react-icons/fi";
import { AppButton } from "@/components/ui/primitives";
import { usePreferencesStore } from "@/stores/usePreferencesStore";
import "./landing.css";

const benefitIcons = [FiCreditCard, FiBarChart2, FiTarget] as const;
const benefitKeys = ["one", "two", "three"] as const;
const stepKeys = ["one", "two", "three"] as const;
const decisionKeys = ["one", "two", "three"] as const;
const faqKeys = ["banks", "analysis", "advice", "taxonomy", "autoCategorize", "money"] as const;

// Sample figures live here, not in the locales, so translators never maintain fake data.
const previewRows = [
  { key: "installments", share: 46 },
  { key: "food", share: 28 },
  { key: "transport", share: 16 },
] as const;

const allocation = [
  { key: "income", share: 52 },
  { key: "fixed", share: 31 },
  { key: "flex", share: 17 },
] as const;

export function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(["landing", "common"]);
  const language = usePreferencesStore((state) => state.language);
  const setLanguageLocal = usePreferencesStore((state) => state.setLanguageLocal);
  const theme = usePreferencesStore((state) => state.theme);
  const setThemeLocal = usePreferencesStore((state) => state.setThemeLocal);

  useEffect(() => {
    const targets = document.querySelectorAll("[data-landing-reveal]");

    // Arm the hidden state from JS so the page stays readable if this never runs.
    targets.forEach((element) => element.classList.add("is-armed"));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 },
    );

    targets.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return (
    <main className="landing-page">
      <header className="landing-nav" aria-label={t("landing.nav.aria")}>
        <a className="landing-brand" href="#inicio" aria-label={t("landing.nav.home")}>
          <img className="landing-brand-mark landing-brand-mark--light" src="/brand/jerry-j-mark-ink.svg" alt="" aria-hidden="true" />
          <img className="landing-brand-mark landing-brand-mark--dark" src="/brand/jerry-j-mark-crema.svg" alt="" aria-hidden="true" />
          <span>Hey Jerry</span>
        </a>

        <nav className="landing-nav-links" aria-label={t("landing.nav.sections")}>
          <a href="#por-que-jerry">{t("landing.nav.why")}</a>
          <a href="#como-funciona">{t("landing.nav.how")}</a>
          <a href="#preguntas">{t("landing.nav.faq")}</a>
        </nav>

        <div className="landing-auth-actions">
          <AppButton tone="ghost" size="sm" onClick={() => navigate({ to: "/auth/signin" })}>
            {t("landing.nav.signIn")}
          </AppButton>
          <AppButton tone="secondary" size="sm" onClick={() => navigate({ to: "/auth/signup" })}>
            {t("landing.nav.signUp")}
          </AppButton>
        </div>
      </header>

      <section className="landing-hero" id="inicio">
        <div className="landing-hero-copy">
          <p className="landing-eyebrow landing-load-in">{t("landing.hero.eyebrow")}</p>
          <h1 className="landing-load-in landing-load-in-delay-1">
            {t("landing.hero.titleLine1")}
            <span>{t("landing.hero.titleLine2")}</span>
          </h1>
          <p className="landing-hero-lead landing-load-in landing-load-in-delay-2">
            {t("landing.hero.lead")}
          </p>
          <div className="landing-hero-actions landing-load-in landing-load-in-delay-3">
            <AppButton tone="primary" size="lg" onClick={() => navigate({ to: "/auth/signup" })}>
              {t("landing.hero.primaryCta")}
              <FiArrowRight aria-hidden="true" />
            </AppButton>
            <a href="#como-funciona" className="landing-text-link">
              {t("landing.hero.secondaryCta")}
            </a>
          </div>
        </div>

        <figure className="landing-preview landing-load-in landing-load-in-delay-2">
          {/* Swap the frame body for <img src="…" alt={t("landing.hero.previewAria")} />
              once a real dashboard screenshot exists. */}
          <div className="landing-preview-frame" role="img" aria-label={t("landing.hero.previewAria")}>
            <div className="landing-preview-body" aria-hidden="true">
              <p className="landing-preview-kicker">{t("landing.preview.sampleData")}</p>
              <p className="landing-preview-window-label">{t("landing.preview.window")}</p>
              <p className="landing-preview-label">{t("landing.preview.balanceLabel")}</p>
              <p className="landing-preview-value">{t("landing.preview.balanceValue")}</p>
              <p className="landing-preview-meta">{t("landing.preview.balanceMeta")}</p>

              <div className="landing-preview-flows">
                <div>
                  <span>{t("landing.preview.inflowLabel")}</span>
                  <strong className="landing-preview-inflow">{t("landing.preview.inflowValue")}</strong>
                </div>
                <div>
                  <span>{t("landing.preview.outflowLabel")}</span>
                  <strong className="landing-preview-outflow">{t("landing.preview.outflowValue")}</strong>
                </div>
              </div>

              <p className="landing-preview-label landing-preview-breakdown-label">
                {t("landing.preview.breakdownLabel")}
              </p>
              <ul className="landing-preview-rows">
                {previewRows.map(({ key, share }) => (
                  <li key={key}>
                    <span>{t(`landing.preview.rows.${key}`)}</span>
                    <i style={{ "--share": `${share}%` } as React.CSSProperties} />
                    <em>{share}%</em>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <figcaption>{t("landing.hero.previewCaption")}</figcaption>
        </figure>
      </section>

      <section className="landing-manifesto" id="por-que-jerry">
        <div className="landing-manifesto-grid">
          <h2 data-landing-reveal>{t("landing.manifesto.title")}</h2>
          <p data-landing-reveal>{t("landing.manifesto.body")}</p>
        </div>

        <div className="landing-benefit-list">
          {benefitKeys.map((key, index) => {
            const Icon = benefitIcons[index];
            return (
              <article
                className="landing-benefit"
                data-landing-reveal
                style={{ "--reveal-delay": `${index * 90}ms` } as React.CSSProperties}
                key={key}
              >
                <span className="landing-benefit-icon"><Icon aria-hidden="true" /></span>
                <h3>{t(`landing.manifesto.benefits.${key}.title`)}</h3>
                <p>{t(`landing.manifesto.benefits.${key}.copy`)}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="landing-process" id="como-funciona">
        <div className="landing-process-copy" data-landing-reveal>
          <h2>{t("landing.process.title")}</h2>
          <p>{t("landing.process.body")}</p>
        </div>

        <ol className="landing-process-flow">
          {stepKeys.map((key, index) => (
            <li
              className="landing-process-step"
              data-landing-reveal
              style={{ "--reveal-delay": `${index * 90}ms` } as React.CSSProperties}
              key={key}
            >
              <span>{t(`landing.process.steps.${key}.kicker`)}</span>
              <strong>{t(`landing.process.steps.${key}.title`)}</strong>
              <small>{t(`landing.process.steps.${key}.copy`)}</small>
            </li>
          ))}
        </ol>
      </section>

      <section className="landing-insight">
        <div className="landing-insight-copy" data-landing-reveal>
          <h2>{t("landing.insight.title")}</h2>
          <p>{t("landing.insight.body")}</p>
          <ol className="landing-insight-list">
            {decisionKeys.map((key, index) => (
              <li key={key}>
                <span className="landing-insight-index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                {t(`landing.insight.decisions.${key}`)}
              </li>
            ))}
          </ol>
        </div>

        <div className="landing-allocation" data-landing-reveal>
          <p className="landing-allocation-label">{t("landing.insight.allocationLabel")}</p>
          <div className="landing-allocation-bar" aria-hidden="true">
            {allocation.map(({ key, share }) => (
              <span
                key={key}
                className={`landing-allocation-segment landing-allocation-${key}`}
                style={{ "--share": `${share}%` } as React.CSSProperties}
              />
            ))}
          </div>
          <ul className="landing-allocation-legend">
            {allocation.map(({ key, share }) => (
              <li key={key}>
                <span className={`landing-allocation-dot landing-allocation-${key}`} aria-hidden="true" />
                {t(`landing.insight.segments.${key}`)}
                <em>{share}%</em>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="landing-faq" id="preguntas">
        <div className="landing-faq-heading" data-landing-reveal>
          <h2>{t("landing.faq.title")}</h2>
          <p>{t("landing.faq.body")}</p>
        </div>

        <div className="landing-faq-list">
          {faqKeys.map((key, index) => (
            <details
              className="landing-faq-item"
              data-landing-reveal
              style={{ "--reveal-delay": `${index * 55}ms` } as React.CSSProperties}
              key={key}
            >
              <summary>
                <span>{t(`landing.faq.items.${key}.question`)}</span>
                <FiChevronDown aria-hidden="true" />
              </summary>
              <p>{t(`landing.faq.items.${key}.answer`)}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="landing-final-cta" data-landing-reveal>
        <div>
          <p className="landing-kicker">{t("landing.finalCta.kicker")}</p>
          <h2>{t("landing.finalCta.title")}</h2>
        </div>
        <div className="landing-final-actions">
          <p>{t("landing.finalCta.body")}</p>
          <AppButton tone="primary" size="lg" onClick={() => navigate({ to: "/auth/signup" })}>
            {t("landing.finalCta.cta")}
            <FiArrowRight aria-hidden="true" />
          </AppButton>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-brand">
          <a className="landing-brand" href="#inicio">
            <img className="landing-brand-mark landing-brand-mark--light" src="/brand/jerry-j-mark-ink.svg" alt="" aria-hidden="true" />
            <img className="landing-brand-mark landing-brand-mark--dark" src="/brand/jerry-j-mark-crema.svg" alt="" aria-hidden="true" />
            <span>Hey Jerry</span>
          </a>
          <p>{t("landing.footer.tagline")}</p>
        </div>

        <p className="landing-footer-note">{t("landing.footer.dataNote")}</p>

        <Link to="/terms" className="justify-self-start text-sm font-semibold text-[var(--app-primary)] underline underline-offset-4 hover:text-[var(--app-primary-hover)]">
          {t("landing.footer.terms")}
        </Link>

        <div className="landing-footer-prefs">
          <fieldset className="landing-switch">
            <legend>{t("landing.footer.language")}</legend>
            <button type="button" aria-pressed={language === "es"} onClick={() => setLanguageLocal("es")}>ES</button>
            <button type="button" aria-pressed={language === "en"} onClick={() => setLanguageLocal("en")}>EN</button>
          </fieldset>

          <fieldset className="landing-switch">
            <legend>{t("landing.footer.theme")}</legend>
            <button type="button" aria-pressed={theme === "light"} onClick={() => setThemeLocal("light")}>
              {t("landing.footer.themeLight")}
            </button>
            <button type="button" aria-pressed={theme === "dark"} onClick={() => setThemeLocal("dark")}>
              {t("landing.footer.themeDark")}
            </button>
          </fieldset>
        </div>
      </footer>
    </main>
  );
}
